import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteFileAttachment } from '../../FileAttachment';
import {
  ClickAnchor,
  ClickMenuContainer,
  ClickMenuItem,
  DownloadFileMenuItemOption,
  Icon,
  Modal,
  ViewFileMenuItemOption,
} from '../../UI';
import { File } from '../../generated/graphql';

export interface FileOptionsProps {
  file: File;
  tenantID: string;
  onDelete?: () => void;
  fileAttachmentID?: string;
  onCancelDelete?: () => void;
  onDeleteSuccess?: () => void;
  children?: React.ReactNode;
  itemID: string | undefined | null;
}

export const FileOptions = ({
  file,
  tenantID,
  onDelete,
  onCancelDelete,
  onDeleteSuccess,
  fileAttachmentID,
  itemID,
}: FileOptionsProps) => {
  const { name, downloadURL, createdBy } = file;
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { t: tFile } = useTranslation('file');
  const { deleteFileAttachment, deleteFileAttachmentMutation } = useDeleteFileAttachment(itemID);

  useEffect(() => {
    if (deleteFileAttachmentMutation.isSuccess) {
      onDeleteSuccess?.();
      setIsOpenModal(false);
    }
  }, [deleteFileAttachmentMutation.isSuccess]);

  return (
    <>
      <ClickAnchor
        inPortal={false}
        placement="bottom-start"
        popperJSPreventOverflowOptions={{ padding: 24 }}
        floatingElement={({ setOpen }) => (
          <ClickMenuContainer className="mr-[-8px] !w-[192px]">
            <ViewFileMenuItemOption downloadURL={downloadURL || ''} fileName={name} />
            <DownloadFileMenuItemOption downloadURL={downloadURL || ''} fileName={name} />
            {fileAttachmentID && (
              <ClickMenuItem
                onClick={() => {
                  setOpen(false);
                  onDelete?.();
                  setIsOpenModal(true);
                }}
              >
                <span className="text-lg font-medium text-white">{tFile('delete-file-label')}</span>
                <Icon icon="delete" className="text-darkMode-danger" />
              </ClickMenuItem>
            )}
          </ClickMenuContainer>
        )}
      >
        <Icon icon="moreHoriz" className="file-options-icon h-6 w-6 hover:text-palette-600" />
        <span className="sr-only">{tFile('file-options-label')}</span>
      </ClickAnchor>
      <Modal
        className="fixed inset-x-0 bottom-16 right-16 z-100 mr-0 w-[542px]"
        cancel={() => {
          onCancelDelete?.();
          setIsOpenModal(false);
        }}
        isOpen={isOpenModal}
        title={tFile('delete-file-modal-title-delete-document', {
          fileName: name,
        })}
        bodyText={tFile('delete-file-modal-body-delete-document', {
          userName: `${createdBy.givenName} ${createdBy.familyName}`,
        })}
        okText={tFile('delete-file-modal-confirm-delete-document')}
        onOk={() => {
          if (fileAttachmentID) {
            deleteFileAttachment.mutate({ id: fileAttachmentID, tenantID });
          }
        }}
        okLoading={deleteFileAttachment.isLoading}
        cancelDisabled={deleteFileAttachment.isLoading}
        okHTMLType="button"
        okThemeType="danger"
      />
    </>
  );
};
